// useColumnVisibility — column show/hide state for a DataTable. Tracks the
// hidden keys (so "all visible" is the default and new columns show by default),
// exposes the filtered `visibleColumns`, and a `menuProps` bundle spreadable
// straight into <ColumnsMenu>. `locked` keys can't be toggled off.

import { useMemo, useState } from 'react';
import type { DataTableColumn } from '@/components/common';

export interface UseColumnVisibilityOptions {
  /** Keys that stay visible and can't be toggled off (listed disabled). */
  locked?: string[];
  /** Keys that stay visible and are left out of the menu entirely. */
  excluded?: string[];
  /** Keys hidden initially. */
  defaultHidden?: string[];
}

export interface ColumnsMenuBinding<T> {
  columns: DataTableColumn<T>[];
  visible: Set<string>;
  onToggle: (key: string) => void;
  lockedKeys: string[];
  excludeKeys: string[];
}

const useColumnVisibility = <T>(
  columns: DataTableColumn<T>[],
  { locked = [], excluded = [], defaultHidden = [] }: UseColumnVisibilityOptions = {},
) => {
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(defaultHidden));

  const toggle = (key: string) => {
    if (locked.includes(key) || excluded.includes(key)) return;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const visible = useMemo(
    () => new Set(columns.map((c) => c.key).filter((k) => !hidden.has(k))),
    [columns, hidden],
  );
  const visibleColumns = useMemo(
    () => columns.filter((c) => visible.has(c.key)),
    [columns, visible],
  );

  const menuProps: ColumnsMenuBinding<T> = {
    columns,
    visible,
    onToggle: toggle,
    lockedKeys: locked,
    excludeKeys: excluded,
  };

  return { visibleColumns, visible, toggle, menuProps };
};

export default useColumnVisibility;
